export type Declension = {
  nominative: string;
  genitive: string;
  dative: string;
  accusative: string;
  instrumental: string;
  prepositional: string;
};

type DeclensionRule = readonly [string, string, string, string, string, string];

const NOMINATIVE = 0;
const GENITIVE = 1;
const DATIVE = 2;
const ACCUSATIVE = 3;
const INSTRUMENTAL = 4;
const PREPOSITIONAL = 5;

const FIRST_NAME_RULES: DeclensionRule[] = [
  ['ия', 'ии', 'ии', 'ию', 'ией', 'ии'],
  ['ья', 'ьи', 'ье', 'ью', 'ьей', 'ье'],
  ['ий', 'ия', 'ию', 'ия', 'ием', 'ии'],
  ['ка', 'ки', 'ке', 'ку', 'кой', 'ке'],
  ['га', 'ги', 'ге', 'гу', 'гой', 'ге'],
  ['ха', 'хи', 'хе', 'ху', 'хой', 'хе'],
  ['жа', 'жи', 'же', 'жу', 'жой', 'же'],
  ['ча', 'чи', 'че', 'чу', 'чой', 'че'],
  ['ша', 'ши', 'ше', 'шу', 'шой', 'ше'],
  ['ща', 'щи', 'ще', 'щу', 'щой', 'ще'],
  ['ел', 'ла', 'лу', 'ла', 'лом', 'ле'],
  ['ев', 'ьва', 'ьву', 'ьва', 'ьвом', 'ьве'],
  ['рь', 'ря', 'рю', 'ря', 'рем', 'ре'],
  ['в', 'ва', 'ву', 'ва', 'вом', 'ве'],
  ['г', 'га', 'гу', 'га', 'гом', 'ге'],
  ['ж', 'жа', 'жу', 'жа', 'жем', 'же'],
  ['х', 'ха', 'ху', 'ха', 'хом', 'хе'],
  ['к', 'ка', 'ку', 'ка', 'ком', 'ке'],
  ['л', 'ла', 'лу', 'ла', 'лом', 'ле'],
  ['м', 'ма', 'му', 'ма', 'мом', 'ме'],
  ['н', 'на', 'ну', 'на', 'ном', 'не'],
  ['п', 'па', 'пу', 'па', 'пом', 'пе'],
  ['р', 'ра', 'ру', 'ра', 'ром', 'ре'],
  ['с', 'са', 'су', 'са', 'сом', 'се'],
  ['т', 'та', 'ту', 'та', 'том', 'те'],
  ['д', 'да', 'ду', 'да', 'дом', 'де'],
  ['ь', 'и', 'и', 'ь', 'ью', 'и'],
  ['я', 'и', 'и', 'ю', 'ей', 'и'],
  ['а', 'ы', 'е', 'у', 'ой', 'е'],
  ['й', 'я', 'ю', 'я', 'ем', 'е'],
];

const MIDDLE_NAME_RULES: DeclensionRule[] = [
  ['ична', 'ичны', 'ичне', 'ичну', 'ичной', 'ичне'],
  ['на', 'ны', 'не', 'ну', 'ной', 'не'],
  ['ич', 'ича', 'ичу', 'ича', 'ичем', 'иче'],
];

const applyRule = (nominative: string, rule: DeclensionRule): DeclensionRule => {
  const base = nominative.slice(0, nominative.length - rule[NOMINATIVE].length);
  return [
    nominative,
    base + rule[GENITIVE],
    base + rule[DATIVE],
    base + rule[ACCUSATIVE],
    base + rule[INSTRUMENTAL],
    base + rule[PREPOSITIONAL],
  ];
};

const declineWord = (nominative: string, rules: DeclensionRule[]): DeclensionRule | null => {
  const normalized = nominative.trim().toLowerCase();
  for (const rule of rules) {
    if (normalized.endsWith(rule[NOMINATIVE])) {
      return applyRule(nominative.trim(), rule);
    }
  }
  return null;
};

export const declineFullName = (firstName: string, middleName: string): Declension | null => {
  const first = declineWord(firstName, FIRST_NAME_RULES);
  const middle = declineWord(middleName, MIDDLE_NAME_RULES);
  if (!first || !middle) {
    return null;
  }
  return {
    nominative: `${first[NOMINATIVE]} ${middle[NOMINATIVE]}`,
    genitive: `${first[GENITIVE]} ${middle[GENITIVE]}`,
    dative: `${first[DATIVE]} ${middle[DATIVE]}`,
    accusative: `${first[ACCUSATIVE]} ${middle[ACCUSATIVE]}`,
    instrumental: `${first[INSTRUMENTAL]} ${middle[INSTRUMENTAL]}`,
    prepositional: `${first[PREPOSITIONAL]} ${middle[PREPOSITIONAL]}`,
  };
};
